import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Settings, Trash2, RotateCcw, Download, Lightbulb, MapPin, Home, Clock, Leaf, Check, Star } from 'lucide-react';
import { useContent, normalizeContentLang } from '../services/contentService';
import { useI18n } from '../i18n';
import ScenarioDescription from './ScenarioDescription';

const SCENARIO_TYPE_MAP = {
    'Agrícola': { icon: Leaf, color: '#1A4731', label: 'Agrícola' },
    'Agroindustrial': { icon: Leaf, color: '#1A4731', label: 'Agroindustrial' },
    'Industrial': { icon: Settings, color: '#44403c', label: 'Industrial' },
    'Construcción': { icon: Home, color: '#92400e', label: 'Construcción' },
    'Infraestructura': { icon: MapPin, color: '#1e40af', label: 'Infraestructura' },
};

const getScenarioType = (tipoProyecto) => {
    if (!tipoProyecto) return SCENARIO_TYPE_MAP['Agrícola'];
    const key = Object.keys(SCENARIO_TYPE_MAP).find(k => tipoProyecto.startsWith(k));
    return SCENARIO_TYPE_MAP[key] || SCENARIO_TYPE_MAP['Agrícola'];
};

const getProjectSubtype = (tipoProyecto) => {
    if (!tipoProyecto) return '';
    const parts = tipoProyecto.split('–');
    return parts.length > 1 ? parts[1].trim() : tipoProyecto;
};

const getVisitMoments = (t) => [
    { id: 'presentacion', label: t('presentation'), description: t('presentationDesc') },
    { id: 'recorrido', label: t('siteVisit'), description: t('siteVisitDesc') },
    { id: 'cierre', label: t('closing'), description: t('closingDesc') },
];

const ProgressRing = ({ percentage, size = 80, strokeWidth = 6 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const pct = typeof percentage === 'number' ? Math.max(0, Math.min(100, percentage)) : 0;
    const offset = circumference - (pct / 100) * circumference;

    return (
        <svg width={size} height={size} className="transform -rotate-90">
            <circle cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke="#E7E5E0" strokeWidth={strokeWidth} />
            <circle cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke="#1A4731" strokeWidth={strokeWidth}
                strokeDasharray={circumference} strokeDashoffset={offset}
                strokeLinecap="round" className="transition-all duration-700" />
            <text x={size / 2} y={size / 2}
                textAnchor="middle" dominantBaseline="central"
                className="fill-stone-800" fontSize="22" fontWeight="700"
                transform={`rotate(90, ${size / 2}, ${size / 2})`}>
                {pct}<tspan fontSize="12" fontWeight="500">%</tspan>
            </text>
        </svg>
    );
};

const formatTimeAgo = (timestamp) => {
    const diff = Math.floor((Date.now() - timestamp) / 1000);
    if (diff < 60) return 'Ahora';
    const mins = Math.floor(diff / 60);
    return `Hace ${mins} min`;
};

const formatElapsed = (startTime) => {
    const secs = Math.floor((Date.now() - startTime) / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const Sidebar = ({
    threadId,
    contentLocale = 'es',
    currentPhase,
    currentSkill,
    evaluatedSkills,
    pendingSkills,
    challengePoolDetail,
    criteriaDetail,
    hint,
    hintHistory = [],
    sessionStartTime,
    turnosDesafio,
    turnosMinimos,
    turnosMaximos,
    progress,
    scenarioInfo,
    selectedSkills,
    tutorialForceHint = false,
    tutorialForceTimeline = false,
    visitStage = '',
    sceneMomentsShown = [],
    currentSceneMomentId = '',
    onLoadMessages,
    onClearHistory,
    onNewThread,
    onBackToScenarios,
    onShowHint,
    hintOverlayVisible = false,
    onExpandedChange,
}) => {
    const { content } = useContent();
    const { t } = useI18n();
    const contentSlice = useMemo(() => {
        if (!content?.byLang) return content;
        const L = normalizeContentLang(contentLocale);
        return content.byLang[L] || content.byLang.es;
    }, [content, contentLocale]);
    const VISIT_MOMENTS = getVisitMoments(t);
    const [isHovered, setIsHovered] = useState(false);
    const [openSection, setOpenSection] = useState(null);
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [showScenarioDetail, setShowScenarioDetail] = useState(false);
    const [newThreadId, setNewThreadId] = useState('');
    const [elapsedTime, setElapsedTime] = useState('00:00');
    const hoverTimeoutRef = useRef(null);

    useEffect(() => {
        if (tutorialForceHint) { setIsHovered(true); setOpenSection('hint'); }
        if (tutorialForceTimeline) { setIsHovered(true); setOpenSection('timeline'); }
    }, [tutorialForceHint, tutorialForceTimeline]);

    useEffect(() => {
        if (!sessionStartTime) return;
        const iv = setInterval(() => setElapsedTime(formatElapsed(sessionStartTime)), 1000);
        return () => clearInterval(iv);
    }, [sessionStartTime]);

    useEffect(() => {
        return () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); };
    }, []);

    const pctNum = typeof progress === 'number' ? Math.round(Math.max(0, Math.min(1, progress)) * 100) : 0;
    const completedCount = challengePoolDetail?.filter(ch => ch.status === 'completed').length || 0;
    const activeCount = challengePoolDetail?.find(ch => ch.status === 'active') ? 1 : 0;
    const totalChallenges = challengePoolDetail?.length || 2;

    const handleMouseEnter = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        if (openSection || isConfigOpen) return;
        hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), 300);
    };

    const toggleSection = (id) => {
        if (openSection === id) { setOpenSection(null); }
        else {
            setOpenSection(id);
            if (id === 'hint' && onShowHint && hint) onShowHint();
        }
    };

    const isExpanded = isHovered || !!openSection || isConfigOpen;

    useEffect(() => {
        onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    const scenarioType = getScenarioType(scenarioInfo?.tipo_proyecto);
    const ScenarioIcon = scenarioType.icon;

    const visitStageToMoment = (vs) => {
        if (!vs) return -1;
        const lower = vs.toLowerCase().replace(/[áéíóú]/g, m => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u' })[m] || m);
        if (['recepcion', 'saludo', 'presentacion', 'presentation', 'conversacion_inicial', 'initial_conversation', 'entrada'].includes(lower)) return 0;
        if (['recorrido', 'walkthrough', 'instalaciones', 'inspeccion', 'oficina'].includes(lower)) return 1;
        if (['cierre', 'closing', 'despedida', 'seguimiento'].includes(lower)) return 2;
        return 0;
    };
    const currentMomentIndex = visitStageToMoment(visitStage);

    const sidebarItems = [
        { id: 'scenario', icon: Home, label: t('scenario'), 'data-tour': 'sidebar-scenario' },
        { id: 'progress', icon: Clock, label: t('progress'), 'data-tour': 'sidebar-progress' },
        { id: 'timeline', icon: MapPin, label: t('route'), 'data-tour': 'sidebar-timeline' },
        { id: 'hint', icon: Lightbulb, label: t('coachHintLabel'), 'data-tour': 'sidebar-hint', hasNotification: !!hint && !hintOverlayVisible },
        { id: 'skills', icon: Star, label: t('tutSkillsTitle'), 'data-tour': 'sidebar-skills' },
    ];

    const getActiveSublabel = (id) => {
        switch (id) {
            case 'scenario': return t('caseDetail');
            case 'progress': return t('challengeXofY', { x: completedCount + activeCount, y: totalChallenges });
            case 'timeline': return t('sessionStages');
            case 'hint': return hintHistory.length > 0 ? t('hintsInChallenge', { n: hintHistory.length, s: hintHistory.length > 1 ? 's' : '' }) : t('noHints');
            case 'skills': return currentSkill || (selectedSkills?.length > 0 ? selectedSkills[0] : '');
            default: return '';
        }
    };

    const renderContent = (id) => {
        switch (id) {
            case 'scenario':
                return (
                    <div className="px-4 pb-4 animate-fade-in">
                        <div className="bg-white/60 rounded-xl border border-stone-200/50 backdrop-blur-sm p-4">
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: scenarioType.color }}>
                                    <ScenarioIcon className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] uppercase tracking-widest font-bold text-stone-400 mb-0.5">{scenarioType.label}</p>
                                    <p className="text-sm font-semibold text-stone-800 leading-snug">{getProjectSubtype(scenarioInfo?.tipo_proyecto)}</p>
                                </div>
                            </div>
                            <p className="text-[12px] text-stone-500 leading-relaxed mb-1">{scenarioInfo?.descripcion_breve || ''}</p>

                            {scenarioInfo?.descripcion_detallada && (
                                <>
                                    {showScenarioDetail && (
                                        <div className="mt-2 mb-1 border-t border-stone-100 pt-2">
                                            <ScenarioDescription
                                                text={scenarioInfo.descripcion_detallada}
                                                className="text-[12px] text-stone-600"
                                            />
                                        </div>
                                    )}
                                    <button
                                        onClick={() => setShowScenarioDetail(v => !v)}
                                        className="text-[11px] font-medium mb-3 transition-colors"
                                        style={{ color: '#1A4731' }}
                                    >
                                        {showScenarioDetail ? t('seeLess') || 'Ver menos ↑' : t('seeMore') || 'Ver más ↓'}
                                    </button>
                                </>
                            )}

                            <div className="flex flex-wrap gap-1.5 mb-3">
                                <span className="text-[10px] px-2 py-0.5 rounded border border-stone-300 text-stone-500 font-medium">IFC PS2</span>
                                <span className="text-[10px] px-2 py-0.5 rounded border border-stone-300 text-stone-500 font-medium">Salud ocupacional</span>
                            </div>
                            {scenarioInfo?.nombre_personaje && (
                                <div className="pt-3 border-t border-stone-200">
                                    <p className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-1">{t('yourInterlocutor')}</p>
                                    <p className="text-xs font-semibold text-stone-700">{scenarioInfo.nombre_personaje}</p>
                                    {scenarioInfo.cargo_personaje && (
                                        <p className="text-[11px] text-stone-400">{scenarioInfo.cargo_personaje}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'progress':
                return (
                    <div className="px-4 pb-4 animate-fade-in">
                        <div className="bg-white/60 rounded-xl border border-stone-200/50 backdrop-blur-sm p-4">
                            <div className="flex justify-center mb-4">
                                <ProgressRing percentage={pctNum} />
                            </div>
                            <div className="space-y-2.5 text-[12px]">
                                <div className="flex justify-between items-center">
                                    <span className="flex items-center gap-2 text-stone-500">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                        {t('activeTime')}
                                    </span>
                                    <span className="font-semibold text-stone-700">{elapsedTime}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="flex items-center gap-2 text-stone-500">
                                        <span className="w-2 h-2 rounded-full bg-stone-400" />
                                        {t('turns')}
                                    </span>
                                    <span className="font-semibold text-stone-700">{t('exchanges', { n: turnosDesafio || 0 })}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="flex items-center gap-2 text-stone-500">
                                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                                        {t('hintsUsed')}
                                    </span>
                                    <span className="font-semibold text-stone-700">{hintHistory.length} de {totalChallenges}</span>
                                </div>
                            </div>
                            <p className="text-[11px] text-stone-400 mt-3 pt-3 border-t border-stone-200 text-center italic">
                                Te falta{totalChallenges - completedCount > 1 ? 'n' : ''} {Math.max(0, totalChallenges - completedCount)} desafío{totalChallenges - completedCount !== 1 ? 's' : ''} para terminar
                            </p>
                        </div>
                    </div>
                );

            case 'timeline':
                return (
                    <div className="px-4 pb-4 animate-fade-in">
                        <p className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-3 px-1">{t('visitMoments')}</p>
                        <ol className="space-y-0 relative">
                            <div className="absolute left-[9px] top-4 bottom-4 w-px bg-stone-200" />
                            {VISIT_MOMENTS.map((moment, i) => {
                                const isPast = currentMomentIndex > i;
                                const isCurrent = currentMomentIndex === i;

                                return (
                                    <li key={moment.id} className="flex items-start gap-3 relative py-2.5">
                                        <div className={`w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 z-10 ${
                                            isCurrent
                                                ? 'ring-2 ring-offset-1'
                                                : isPast
                                                    ? 'bg-stone-600'
                                                    : 'bg-stone-200'
                                        }`} style={isCurrent ? { backgroundColor: '#1A4731', ringColor: 'rgba(26,71,49,0.3)' } : {}}>
                                            {isPast && <Check className="w-2.5 h-2.5 text-white" />}
                                            {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                        </div>
                                        <div className="flex-1 min-w-0 -mt-0.5">
                                            <p className={`text-[13px] font-semibold leading-tight ${
                                                isCurrent ? 'text-stone-800' : isPast ? 'text-stone-700' : 'text-stone-400'
                                            }`}>{moment.label}</p>
                                            <p className={`text-[11px] leading-snug mt-0.5 ${
                                                isCurrent ? 'text-stone-500' : isPast ? 'text-stone-400' : 'text-stone-300'
                                            }`}>{moment.description}</p>
                                            {isCurrent && (
                                                    <p className="text-[10px] font-semibold mt-1" style={{ color: '#1A4731' }}>{t('inProgress')}</p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                );

            case 'hint':
                return (
                    <div className="px-4 pb-4 animate-fade-in space-y-2">
                        {hintHistory.length > 0 ? (
                            <>
                                {[...hintHistory].reverse().map((h, i) => {
                                    const isActive = h.active;
                                    return (
                                        <div key={i} className={`p-3 rounded-xl border ${
                                            isActive
                                                ? 'bg-emerald-50 border-emerald-200'
                                                : 'bg-stone-50 border-stone-200'
                                        }`}>
                                            <div className="flex items-center gap-1.5 mb-1.5">
                                                {isActive ? (
                                                    <>
                                                        <Lightbulb className="w-3 h-3 text-emerald-600" />
                                                        <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{t('activeNow')}</span>
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-0.5" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <Check className="w-3 h-3 text-stone-400" />
                                                        <span className="text-[10px] uppercase tracking-widest font-bold text-stone-400">Vista {formatTimeAgo(h.timestamp).toLowerCase()}</span>
                                                    </>
                                                )}
                                            </div>
                                            <p className="text-[13px] text-stone-700 leading-relaxed">{h.text}</p>
                                        </div>
                                    );
                                })}
                                {totalChallenges - hintHistory.length > 0 && (
                                    <div className="p-3 rounded-xl border border-dashed border-stone-200">
                                                <p className="text-[10px] uppercase tracking-widest font-bold text-stone-300 text-center">{t('toDiscover')}</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="p-4 bg-white/60 rounded-xl border border-stone-200/50 backdrop-blur-sm">
                                <p className="text-[11px] text-stone-400 italic text-center">{t('noHintsYet')}</p>
                            </div>
                        )}
                    </div>
                );

            case 'skills': {
                const allSkills = selectedSkills?.length > 0 ? selectedSkills : (currentSkill ? [currentSkill] : []);
                if (allSkills.length === 0) return (
                    <div className="px-4 pb-4 animate-fade-in">
                        <div className="p-4 bg-white/60 rounded-xl border border-stone-200/50 backdrop-blur-sm">
                            <p className="text-[11px] text-stone-400 italic text-center">{t('selectSkillToContinue')}</p>
                        </div>
                    </div>
                );
                return (
                    <div className="px-4 pb-4 animate-fade-in space-y-2">
                        {allSkills.map((skillKey) => {
                            const isEvaluated = evaluatedSkills?.includes(skillKey);
                            const isCurrent = currentSkill === skillKey;
                            const isPending = pendingSkills?.includes(skillKey) || (!isEvaluated && !isCurrent);
                            const skillData = contentSlice?.skills?.[skillKey];
                            const displayName = skillData?.nombre || skillKey;
                            return (
                                <div key={skillKey} className={`flex items-start gap-3 p-3 rounded-xl border ${
                                    isEvaluated
                                        ? 'bg-emerald-50 border-emerald-200'
                                        : isCurrent
                                            ? 'bg-white border-stone-300 shadow-sm'
                                            : 'bg-stone-50/60 border-stone-200/50'
                                }`}>
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                                        isEvaluated ? 'bg-emerald-500' : isCurrent ? '' : 'bg-stone-200'
                                    }`} style={isCurrent ? { background: '#1A4731' } : {}}>
                                        {isEvaluated
                                            ? <Check className="w-3.5 h-3.5 text-white" />
                                            : <Star className="w-3.5 h-3.5 text-white" />
                                        }
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-[12px] font-semibold leading-snug ${
                                            isEvaluated ? 'text-emerald-800' : isCurrent ? 'text-stone-800' : 'text-stone-400'
                                        }`}>{displayName}</p>
                                        <p className={`text-[10px] uppercase tracking-widest font-bold mt-0.5 ${
                                            isEvaluated ? 'text-emerald-600' : isCurrent ? '' : 'text-stone-300'
                                        }`} style={isCurrent && !isEvaluated ? { color: '#1A4731' } : {}}>
                                            {isEvaluated ? t('progress') : isCurrent ? t('inProgress') : t('toDiscover')}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            default: return null;
        }
    };

    return (
        <div className="z-[5] flex shrink-0 items-stretch self-stretch py-3 pl-0 pr-0.5" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div
                className="flex flex-col overflow-hidden transition-all duration-300 ease-out"
                style={{
                    width: isExpanded ? '280px' : '56px',
                    background: 'linear-gradient(180deg, rgba(240, 244, 238, 0.7) 0%, rgba(246, 248, 244, 0.85) 100%)',
                    border: '0.5px solid rgba(58, 74, 41, 0.1)',
                    boxShadow: 'inset 0 1px 1px rgba(58, 74, 41, 0.04), inset 0 -1px 0 rgba(255, 255, 255, 0.65)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '12px',
                }}
            >
                <div className="flex-1 overflow-y-auto py-4">
                    {sidebarItems.map(item => {
                        const Icon = item.icon;
                        const isOpen = openSection === item.id;

                        return (
                            <div key={item.id}>
                                <button
                                    data-tour={item['data-tour']}
                                    onClick={() => toggleSection(item.id)}
                                    className={`w-full flex items-center transition-colors ${
                                        isExpanded ? 'px-4 py-2.5 gap-3' : 'px-0 py-2.5 justify-center'
                                    } ${isOpen ? 'bg-white/50' : 'hover:bg-white/30'}`}
                                >
                                    <div className="relative shrink-0 w-8 h-8 flex items-center justify-center">
                                        <Icon className={`w-[18px] h-[18px] transition-colors ${
                                            isOpen ? 'text-stone-700' : 'text-stone-400'
                                        }`} />
                                        {item.hasNotification && !isExpanded && (
                                            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500" />
                                        )}
                                    </div>

                                    {isExpanded && (
                                        <div className="flex-1 min-w-0 text-left flex items-center justify-between">
                                            <div>
                                                <p className={`text-[13px] font-semibold leading-tight ${
                                                    isOpen ? 'text-stone-800' : 'text-stone-600'
                                                }`}>{item.label}</p>
                                                {isOpen && (
                                                    <p className="text-[11px] font-medium" style={{ color: '#1A4731' }}>
                                                        {getActiveSublabel(item.id)}
                                                    </p>
                                                )}
                                            </div>
                                            {item.hasNotification && isExpanded && !isOpen && (
                                                <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                                            )}
                                        </div>
                                    )}
                                </button>

                                {isOpen && isExpanded && renderContent(item.id)}
                            </div>
                        );
                    })}
                </div>

                {/* Ajustes */}
                <div className={`border-t border-stone-200/40 ${isExpanded ? 'p-3' : 'p-2 flex justify-center'}`}>
                    {isExpanded ? (
                        <>
                            <button
                                onClick={() => setIsConfigOpen(!isConfigOpen)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-stone-400 hover:text-stone-700 hover:bg-white/40 rounded-lg transition-colors"
                            >
                                <Settings className="w-[18px] h-[18px]" />
                                <span className="text-[13px] font-semibold text-stone-600">{t('settings')}</span>
                            </button>

                            {isConfigOpen && (
                                <div className="mt-2 space-y-2 p-3 bg-white/60 rounded-xl border border-stone-200/50 backdrop-blur-sm">
                                    <div className="text-[10px] text-stone-400 break-all mb-2 font-mono">{threadId}</div>
                                    <div className="flex gap-1.5">
                                        <input type="text" value={newThreadId} onChange={(e) => setNewThreadId(e.target.value)}
                                            placeholder="Thread ID"
                                            className="flex-1 px-2.5 py-1.5 bg-white border border-stone-200 rounded-lg text-[10px] text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-300" />
                                        <button onClick={() => onLoadMessages(newThreadId)}
                                            className="bg-white/80 hover:bg-white text-stone-600 px-2.5 py-1.5 rounded-lg text-[10px] transition-colors">
                                            <Download className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button onClick={onNewThread}
                                            className="flex-1 bg-white/60 hover:bg-white text-stone-600 py-2 rounded-lg text-[10px] transition-colors flex items-center justify-center gap-1">
                                            <RotateCcw className="w-2.5 h-2.5" /> {t('newThread')}
                                        </button>
                                        <button onClick={onClearHistory}
                                            className="flex-1 bg-red-50/80 hover:bg-red-100 text-red-600 py-2 rounded-lg text-[10px] transition-colors flex items-center justify-center gap-1 border border-red-200/50">
                                            <Trash2 className="w-2.5 h-2.5" /> {t('clean')}
                                        </button>
                                    </div>
                                    <button onClick={onBackToScenarios}
                                        className="w-full bg-white/60 hover:bg-white text-stone-600 py-2 rounded-lg text-[10px] transition-colors">
                                        {t('changeScenario')}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <button onClick={() => { setIsHovered(true); setIsConfigOpen(true); }}
                            className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 hover:bg-white/40 rounded-lg transition-colors"
                            title="Ajustes">
                            <Settings className="w-[18px] h-[18px]" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
